'use client';

import { contentImageAlt } from '@communio/content-image-alt';

import { FormEvent, useEffect, useState } from 'react';
import { OrderMessageThread } from '@/platform/commerce/OrderMessageThread';
import {
  formatOrderPrice,
  formatShippingAddress,
  getPayoutSummary,
  getVendorOrder,
  listVendorOrders,
  refundVendorOrder,
  updateVendorOrderFulfillment,
  type VendorOrder,
} from '@/platform/commerce/api';
import { createExpressLoginLink } from './api';

export interface VendorOrdersPanelProps {
  vendorUserId: string;
}

export function VendorOrdersPanel({ vendorUserId }: VendorOrdersPanelProps) {
  const [orders, setOrders] = useState<VendorOrder[]>([]);
  const [selected, setSelected] = useState<VendorOrder | null>(null);
  const [payout, setPayout] = useState<{ availableCents: number; pendingCents: number; currency: string } | null>(
    null,
  );
  const [trackingCarrier, setTrackingCarrier] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      listVendorOrders(vendorUserId).then((r) => setOrders(r.orders)),
      getPayoutSummary(vendorUserId)
        .then(setPayout)
        .catch(() => setPayout(null)),
    ])
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [vendorUserId]);

  useEffect(() => {
    if (!selected) return;
    setTrackingCarrier(selected.trackingCarrier ?? '');
    setTrackingNumber(selected.trackingNumber ?? '');
  }, [selected]);

  function selectOrder(order: VendorOrder) {
    setSelected(order);
    setDetailLoading(true);
    setError('');
    getVendorOrder(vendorUserId, order.id)
      .then(setSelected)
      .catch((err: Error) => setError(err.message))
      .finally(() => setDetailLoading(false));
  }

  async function markShipped(e: FormEvent) {
    e.preventDefault();
    if (!selected) return;
    setError('');
    try {
      const updated = await updateVendorOrderFulfillment(vendorUserId, selected.id, {
        status: 'shipped',
        trackingCarrier,
        trackingNumber,
      });
      setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
      setSelected(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update order');
    }
  }

  async function markDelivered() {
    if (!selected) return;
    setError('');
    try {
      const updated = await updateVendorOrderFulfillment(vendorUserId, selected.id, {
        status: 'delivered',
      });
      setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
      setSelected(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not mark order delivered');
    }
  }

  async function issueRefund() {
    if (!selected) return;
    if (!window.confirm('Issue a full refund to the buyer via Stripe?')) return;
    setError('');
    try {
      const updated = await refundVendorOrder(vendorUserId, selected.id);
      setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
      setSelected(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not refund order');
    }
  }

  if (loading) return <p>Loading orders…</p>;

  return (
    <section>
      <h2 className="vendor-console__section-title">Orders</h2>
      {payout && (
        <p className="vendor-console__hint">
          Payout balance: {formatOrderPrice(payout.availableCents, payout.currency.toUpperCase())} available,{' '}
          {formatOrderPrice(payout.pendingCents, payout.currency.toUpperCase())} pending{' '}
          <button
            type="button"
            className="btn btn-link"
            onClick={() => {
              void createExpressLoginLink(vendorUserId)
                .then((r) => {
                  window.open(r.url, '_blank', 'noopener,noreferrer');
                })
                .catch((err: Error) => setError(err.message));
            }}
          >
            Open Stripe Express
          </button>
        </p>
      )}
      {error && <p className="form-error">{error}</p>}
      {orders.length === 0 ? (
        <p className="text-muted">No orders yet. Active products with hosted checkout appear here when buyers purchase.</p>
      ) : (
        <div className="vendor-orders__layout">
          <ul className="vendor-orders__list">
            {orders.map((order) => (
              <li key={order.id} className="vendor-orders__list-item">
                <button type="button" className="btn btn-link vendor-orders__list-button" onClick={() => selectOrder(order)}>
                  {order.lineItems[0]?.imageUrl && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={order.lineItems[0].imageUrl} alt={contentImageAlt(order.lineItems[0].name ?? 'Order', 'product photo')} className="order-line__thumb" loading="lazy" />
                  )}
                  <span>
                    {order.lineItems[0]?.name ?? 'Order'} — {order.status} —{' '}
                    {formatOrderPrice(order.totalCents, order.currency)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {selected && (
            <article className="vendor-order-detail feed-card settings-page">
              <h3>Order {selected.id.slice(0, 8)}</h3>
              {detailLoading && <p className="text-muted">Refreshing order…</p>}
              <p>Status: {selected.status}</p>
              <p>Buyer: {selected.buyerEmail || 'Registered buyer'}</p>
              <h4>Items</h4>
              <ul>
                {selected.lineItems.map((item) => (
                  <li key={item.productId} className="vendor-order-detail__line">
                    {item.imageUrl && (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={item.imageUrl} alt={contentImageAlt(item.name, 'product photo')} className="order-line__thumb" loading="lazy" />
                    )}
                    <span>
                      {item.name} × {item.quantity} —{' '}
                      {formatOrderPrice(item.unitPriceCents * item.quantity, selected.currency)}
                    </span>
                  </li>
                ))}
              </ul>
              <h4>Totals</h4>
              <p>Subtotal: {formatOrderPrice(selected.subtotalCents, selected.currency)}</p>
              {selected.shippingCents != null && selected.shippingCents > 0 && (
                <p>
                  Shipping{selected.shippingOptionLabel ? ` (${selected.shippingOptionLabel})` : ''}:{' '}
                  {formatOrderPrice(selected.shippingCents, selected.currency)}
                </p>
              )}
              {selected.taxCents != null && selected.taxCents > 0 && (
                <p>Tax: {formatOrderPrice(selected.taxCents, selected.currency)}</p>
              )}
              <p>
                <strong>Total: {formatOrderPrice(selected.totalCents, selected.currency)}</strong>
              </p>
              {selected.shippingAddress && (
                <>
                  <h4>Ship to</h4>
                  <pre className="buyer-order-detail__address">{formatShippingAddress(selected.shippingAddress)}</pre>
                </>
              )}
              {selected.status === 'paid' && (
                <form onSubmit={(e) => void markShipped(e)} className="vendor-console__form-grid">
                  <label>
                    Carrier
                    <input value={trackingCarrier} onChange={(e) => setTrackingCarrier(e.target.value)} />
                  </label>
                  <label>
                    Tracking number
                    <input value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} />
                  </label>
                  <button type="submit" className="btn btn-primary">
                    Mark shipped
                  </button>
                </form>
              )}
              {selected.status === 'shipped' && (
                <button type="button" className="btn btn-primary" onClick={() => void markDelivered()}>
                  Mark delivered
                </button>
              )}
              {(selected.status === 'paid' || selected.status === 'shipped') && (
                <button type="button" className="btn btn-secondary" onClick={() => void issueRefund()}>
                  Issue refund
                </button>
              )}
              <OrderMessageThread orderId={selected.id} canCompose />
            </article>
          )}
        </div>
      )}
    </section>
  );
}
